/**
 * AI API Client
 *
 * Typed API client for AI suggestion endpoints with automatic error handling.
 * Provides methods for suggestions, feedback, and apply operations.
 *
 * @module features/ai-button/api/aiApiClient
 * @see SPEC-012 Requirements 1.1, 8.1, 9.1
 */

import { ApiError, ApiErrorCode } from '@linkode/shared';
import type {
  SuggestRequest,
  SuggestResponse,
  FeedbackRequest,
  FeedbackResponse,
  ApplyRequest,
  ApplyResponse,
} from '@linkode/shared';
import { useAuthStore } from '../../auth/store/authStore';

// ============================================================================
// Constants
// ============================================================================

/** API base URL from environment variable */
const API_BASE_URL =
  import.meta.env.VITE_API_URL ||
  (import.meta.env.DEV ? 'http://localhost:8787' : 'https://api-staging.getlinkode.com');

// ============================================================================
// DevPanel Integration (DEV only)
// ============================================================================

/**
 * Record AI suggest request to DevPanel (DEV only).
 * This function is tree-shaken in production builds.
 */
function recordToDevPanel(
  docId: string,
  startTime: number,
  response: SuggestResponse | null,
  error?: string
): void {
  if (!import.meta.env.DEV) return;

  // Dynamic import to avoid bundling in production
  import('../../../devpanel/hooks/useAiButtonPanel')
    .then(({ recordAiSuggestRequest }) => {
      const endTime = Date.now();
      recordAiSuggestRequest({
        docId,
        proposalId: response?.proposalId ?? 'error',
        latency: {
          total: endTime - startTime,
          loadDoc: 0, // Not available client-side
          buildQueries: 0,
          vectorQuery: 0,
          graphFetch: 0,
          scoring: 0,
        },
        backlinkCount: response?.backlinks?.length ?? 0,
        tagCount: response?.tags?.length ?? 0,
        degraded: response?.degraded ?? false,
        degradeReason: response?.degradeReason,
        error,
        // SPEC-019 Requirement 15.2: Include raw response for debugging
        rawResponse: response,
      });
    })
    .catch(() => {
      // Silently ignore if DevPanel not available
    });
}

/**
 * Record feedback to DevPanel (DEV only).
 * SPEC-019 Requirement 15.5
 */
function recordFeedbackToDevPanel(
  proposalId: string,
  candidateId: string,
  kind: 'backlink' | 'tag',
  action: 'up' | 'down'
): void {
  if (!import.meta.env.DEV) return;

  import('../../../devpanel/hooks/useAiButtonPanel')
    .then(({ recordAiFeedback }) => {
      recordAiFeedback({
        proposalId,
        candidateId,
        kind,
        action,
      });
    })
    .catch(() => {
      // Silently ignore if DevPanel not available
    });
}

// ============================================================================
// API Client Interface
// ============================================================================

/**
 * AI API Client Interface
 *
 * Defines the contract for AI suggestion API operations.
 *
 * @see Requirements 1.1, 8.1, 9.1
 */
export interface AiApiClient {
  /**
   * Get AI suggestions for a document
   *
   * @param docId - Document ID to get suggestions for
   * @returns Promise resolving to suggestion response
   * @throws ApiError on failure
   */
  getSuggestions(docId: string): Promise<SuggestResponse>;

  /**
   * Submit feedback for a suggestion
   *
   * @param request - Feedback request
   * @returns Promise resolving to feedback response
   * @throws ApiError on failure
   */
  submitFeedback(request: FeedbackRequest): Promise<FeedbackResponse>;

  /**
   * Apply selected suggestions
   *
   * @param request - Apply request
   * @returns Promise resolving to apply response
   * @throws ApiError on failure
   */
  applySuggestions(request: ApplyRequest): Promise<ApplyResponse>;
}

// ============================================================================
// API Client Implementation
// ============================================================================

/**
 * AI API Client Implementation
 *
 * Handles all AI-related API calls with:
 * - Automatic Authorization header injection
 * - Unified error handling
 * - UNAUTHORIZED auto-logout
 *
 * @see Requirements 1.1, 8.1, 9.1
 */
class AiApiClientImpl implements AiApiClient {
  /**
   * Get Authorization header if session token exists
   */
  private getAuthHeader(): HeadersInit {
    const token = useAuthStore.getState().sessionToken;
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /**
   * Handle API response with unified error handling
   */
  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      let error: ApiError;

      try {
        error = (await response.json()) as ApiError;
      } catch {
        error = {
          code: ApiErrorCode.INTERNAL_ERROR,
          message: 'An unexpected error occurred',
          requestId: 'unknown',
        };
      }

      // Handle UNAUTHORIZED - trigger state clear
      if (error.code === ApiErrorCode.UNAUTHORIZED) {
        useAuthStore.setState({
          isAuthenticated: false,
          userId: null,
          email: null,
          sessionToken: null,
          expiresAt: null,
          error: null,
          isLoading: false,
        });
      }

      throw error;
    }

    return response.json();
  }

  async getSuggestions(docId: string): Promise<SuggestResponse> {
    const startTime = Date.now();
    const request: SuggestRequest = { docId };

    try {
      const response = await fetch(`${API_BASE_URL}/ai/suggest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeader(),
        },
        body: JSON.stringify(request),
      });

      const result = await this.handleResponse<SuggestResponse>(response);

      // Record to DevPanel (DEV only)
      recordToDevPanel(docId, startTime, result);

      return result;
    } catch (err) {
      // Record error to DevPanel (DEV only)
      const errorMessage =
        err instanceof Error ? err.message : ((err as ApiError)?.message ?? 'Unknown error');
      recordToDevPanel(docId, startTime, null, errorMessage);
      throw err;
    }
  }

  async submitFeedback(request: FeedbackRequest): Promise<FeedbackResponse> {
    const response = await fetch(`${API_BASE_URL}/ai/feedback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify(request),
    });

    const result = await this.handleResponse<FeedbackResponse>(response);

    // Record feedback to DevPanel (DEV only, SPEC-019 Requirement 15.5)
    recordFeedbackToDevPanel(request.proposalId, request.candidateId, request.kind, request.action);

    return result;
  }

  async applySuggestions(request: ApplyRequest): Promise<ApplyResponse> {
    const response = await fetch(`${API_BASE_URL}/ai/apply`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
      },
      body: JSON.stringify(request),
    });

    return this.handleResponse<ApplyResponse>(response);
  }
}

// ============================================================================
// Singleton Export
// ============================================================================

/**
 * Singleton instance of AI API Client
 */
export const aiApiClient: AiApiClient = new AiApiClientImpl();
